'use client';
import React from 'react';
import * as Styled from './styled';
import { Stack } from '@/ui/primatives';
import useDataframeContext from '../../../DataframeContext';
import { IDataFrameField } from '@/lib/dataFrameTypes';
import { SCHEMA_DISPLAY_FIELD_NAME_BLACKLIST } from '@/components/DataFrame/consts';
import useDataframeQueryContext from '@/components/DataFrame/DataframeQueryContext';

import { TKeyValuePair } from '@/types';

import { AddColumn } from '../ColumnOperations';
import FieldView from './FieldView';

const useFields = (
  sourceData: any,
  viewData: any,
): { sourceFields: IDataFrameField[]; viewFields: IDataFrameField[]; derivedFields: IDataFrameField[] } => {
  const sourceFields = sourceData?.schema.fields;
  const viewFields = viewData.schema.fields;
  const derivedFields = viewFields.filter(
    (field: TKeyValuePair) => !sourceFields.map((field: TKeyValuePair) => field.name).includes(field.name),
  );
  return { sourceFields, viewFields, derivedFields };
};

const SchemaDisplay: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const { isLoading, viewData, sourceData, editable } = useDataframeContext();
  const { onQueryColumnsChange } = useDataframeQueryContext();

  if (isLoading && !viewData && !sourceData) {
    return <Styled.SchemaRail>Loading...</Styled.SchemaRail>;
  }

  const { sourceFields, viewFields, derivedFields } = useFields(sourceData, viewData);

  const viewFieldNames = viewFields.map((field: TKeyValuePair) => field.name);
  const addedFieldNames = derivedFields.map((field: TKeyValuePair) => field.name);

  const allFields = [...sourceFields, ...derivedFields];

  const query = new URLSearchParams(window.location.search);
  const queryColumns = !!query.get('columns') ? query.get('columns')?.split(',') : [];
  const visibleColumns = !!queryColumns?.length ? queryColumns : viewFieldNames;

  const sortQueryColumnsToMatchSchema = (columns: string[]) => {
    const sourceFieldNames = sourceFields.map((field: TKeyValuePair) => field.name);
    const sortedColumns = sourceFieldNames.filter((field: string) => columns.includes(field));
    return sortedColumns;
  };

  const onToggleColumn = (fieldName: string) => {
    const columns = visibleColumns;
    if (columns.includes(fieldName)) {
      columns.splice(columns.indexOf(fieldName), 1);
    } else {
      columns.push(fieldName);
    }

    // NOTE: This cannot not support derrived fields
    const sortedColumns = sortQueryColumnsToMatchSchema(columns);
    onQueryColumnsChange(sortedColumns);
  };

  // console.log('allFields', allFields);
  return (
    <Styled.SchemaRail>
      <Stack gap={5}>
        {allFields
          .filter((field: IDataFrameField) => !SCHEMA_DISPLAY_FIELD_NAME_BLACKLIST.includes(field.name))
          .map((field: IDataFrameField) => {
            return (
              field && (
                <FieldView
                  key={field.name}
                  field={field}
                  viewFieldNames={viewFieldNames}
                  addedFieldNames={addedFieldNames}
                  onToggle={onToggleColumn}
                />
              )
            );
          })}
      </Stack>
      {editable && <AddColumn />}
      {children}
    </Styled.SchemaRail>
  );
};

export default SchemaDisplay;
